// Report staleness — the reader-facing half of the API's staleness reasons
// (see tdp-api utils/reportStaleness.ts, which produces these codes).
//
// A stored report is a frozen PDF. When anything it drew on has since moved,
// the UI says so — and says WHAT moved, because "Stale" alone leaves the reader
// to guess whether a photo was pulled for safeguarding or the logo changed.
//
// Two tiers, because those two are not the same news:
//
//   content  — the report's substance is now wrong: a post is missing, a photo
//              changed, the template it was built from moved. Regenerating is
//              corrective. Wears the clay warning treatment.
//   cosmetic — the substance is right and only the letterhead is dated.
//              Regenerating is optional. Deliberately quiet: branding changes
//              hit EVERY report in the org at once, so if they wore the warning
//              colour a single logo change would turn the whole table amber and
//              the warning would stop meaning anything.
//
// A report carrying both tiers is content-stale — the more consequential tier
// always wins the pill, and the cosmetic reason still shows in the full list.

export type StaleReason =
  | 'posts_removed'
  | 'posts_added'
  | 'post_republished'
  | 'photos_changed'
  | 'photos_edited'
  | 'template_changed'
  | 'template_deleted'
  | 'branding_changed';

export type StaleTier = 'content' | 'cosmetic';

interface StaleReasonMeta {
  tier: StaleTier;
  /** Pill text when this is the leading reason. Short — it sits in a table cell. */
  label: string;
  /** Full sentence for the hover list. */
  detail: string;
}

export const STALE_REASONS: Record<StaleReason, StaleReasonMeta> = {
  posts_removed: {
    tier: 'content',
    label: 'Post removed',
    detail: 'A post in this report has since been unpublished or deleted.',
  },
  photos_changed: {
    tier: 'content',
    label: 'Photos changed',
    detail: 'Photos have been added, removed, or held back from the report.',
  },
  photos_edited: {
    tier: 'content',
    label: 'Blurring changed',
    detail: "A photo's face-blurring has been applied or changed.",
  },
  post_republished: {
    tier: 'content',
    label: 'Post republished',
    detail:
      'A post was republished after this report — its approved wording or photos may have changed.',
  },
  posts_added: {
    tier: 'content',
    label: 'New posts',
    detail: 'Posts have been published into this period since the report was generated.',
  },
  template_deleted: {
    tier: 'content',
    label: 'Template deleted',
    detail:
      'The report template has been deleted — regenerating will fall back to the standard layout.',
  },
  template_changed: {
    tier: 'content',
    label: 'Template changed',
    detail: 'The report template has been edited since this report was generated.',
  },
  branding_changed: {
    tier: 'cosmetic',
    label: 'Branding updated',
    detail: "Your organisation's name, logo, or brand colour has changed.",
  },
};

/** Drop codes this build doesn't know, so a newer API can't break the pill. */
const known = (reasons: string[]): StaleReason[] =>
  reasons.filter((r): r is StaleReason => r in STALE_REASONS);

/**
 * Which treatment the pill wears, or null when the report is up to date.
 * Content outranks cosmetic regardless of the order reasons arrive in.
 */
export function staleTier(reasons: string[] = []): StaleTier | null {
  const list = known(reasons);
  if (list.length === 0) return null;
  return list.some(r => STALE_REASONS[r].tier === 'content') ? 'content' : 'cosmetic';
}

/**
 * Pill text: the leading reason when there's one thing to say, a count when
 * there are several. The API returns reasons most- to least-consequential, so
 * the first known one is the one worth naming.
 */
export function stalePillLabel(reasons: string[] = []): string {
  const list = known(reasons);
  if (list.length === 0) return 'Current';
  if (list.length === 1) return STALE_REASONS[list[0]].label;
  return `${list.length} changes`;
}

/** Full sentences for the tooltip, in the API's priority order. */
export function staleDetails(reasons: string[] = []): string[] {
  return known(reasons).map(r => STALE_REASONS[r].detail);
}

/** Tooltip body — one reason per line. */
export function staleTooltip(reasons: string[] = []): string {
  const details = staleDetails(reasons);
  return details.length > 0
    ? details.join('\n')
    : 'This report matches the posts, photos and template it was built from.';
}

// ── Unsaved header-field edits ───────────────────────────────────────────────
// Distinct from the reasons above, and deliberately NOT one of them: those
// describe stored data that has drifted, whereas this is local, unsaved panel
// state. Putting it in the pill would mean navigating away silently "fixed" the
// staleness without anything actually changing.

/**
 * What a header field will render as, given a set of panel entries: an entry
 * wins; an untouched field falls back to its pre-fill (resolved default or
 * computed value) and then the template's own default. The same precedence
 * ActionPanel displays with and the report resolver renders with, so anything
 * measured through this agrees with what the reader sees.
 */
export function resolveFieldEntry(
  field: { key: string; defaultValue?: string },
  entries: Record<string, string>,
  defaults: Record<string, string> = {},
): string {
  return ((field.key in entries ? entries[field.key] : (defaults[field.key] ?? field.defaultValue)) ?? '').trim();
}

/**
 * Which fields would make the PDF differ if it were regenerated now — i.e. the
 * ones the user has edited since it was written.
 *
 * Both sides are RESOLVED rather than compared as raw entries. Most stored
 * reports hold no entry for a field at all (it rendered from the template
 * default), so comparing entries meant typing a value back to what the report
 * actually shows still counted as an edit, and "changed it back" could never
 * clear. Resolving both sides also stops a since-edited template default being
 * misreported as a user edit: both fall back to the same current default, so an
 * untouched field always matches. A changed template is the stale reasons'
 * business, not this one's.
 */
export function changedFieldEntries<T extends { key: string; label: string; defaultValue?: string }>(
  fields: T[],
  entries: Record<string, string>,
  storedEntries: Record<string, string>,
  defaults: Record<string, string> = {},
): T[] {
  return fields.filter(f =>
    resolveFieldEntry(f, entries, defaults) !== resolveFieldEntry(f, storedEntries, defaults));
}
